import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, EyeIcon, DocumentTextIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, getImageUrl, unitApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface Lesson {
  id: number;
  title: string;
  type: 'video' | 'quiz' | 'file' | 'assignment';
  isVisible: boolean;
  order?: number;
}

interface Unit {
  id: number;
  unitName: string;
  titel: string;
  unitTitle: string;
  price: number;
  imagePath: string | null;
  courseId: string | null;
  teacherId: string | null;
  gradeId: string | null;
  active: boolean;
  order: number;
  creationDate: string;
  isIndividual: boolean;
}

interface LessonFile {
  fileName: string;
  fileUrl: string;
  order: number;
}

interface Question {
  id: number;
  text: string;
  options: string[];
  correctOptionIndex: number | null;
  type: 'MultipleChoice' | 'TrueFalse';
}

interface StudentFilters {
  searchTerm: string;
  name: string;
}

const LectureDetail = () => {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSaeedAdmin } = useUser();
  const canViewStudentCounts = isSaeedAdmin();
  
  const [activeTab, setActiveTab] = useState<'content' | 'details' | 'students'>('content');
  const [unit, setUnit] = useState<Unit | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Students state
  const [enrolledStudents, setEnrolledStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  const [studentFilters, setStudentFilters] = useState<StudentFilters>({
    searchTerm: '',
    name: ''
  });
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [studentPageSize] = useState(10);
  const [studentTotalPages, setStudentTotalPages] = useState(1);
  const [studentTotalCount, setStudentTotalCount] = useState(0);
  
  // Enroll student state
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollStudentData, setEnrollStudentData] = useState('');
  const [enrollStudentError, setEnrollStudentError] = useState<string | null>(null);
  const [searchedStudent, setSearchedStudent] = useState<any | null>(null);
  const [isSearchingStudent, setIsSearchingStudent] = useState(false);
  
  // Modal states
  const [showLessonTypeModal, setShowLessonTypeModal] = useState(false);
  const [showDeleteLessonModal, setShowDeleteLessonModal] = useState(false);
  const [showHideLessonModal, setShowHideLessonModal] = useState(false);
  const [lessonToAction, setLessonToAction] = useState<{lessonId: number} | null>(null);
  
  // Video lesson states
  const [showVideoLessonForm, setShowVideoLessonForm] = useState(false);
  const [videoLessonTitle, setVideoLessonTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  
  // Quiz lesson states
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [quizTitle, setQuizTitle] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    id: 1,
    text: '',
    options: ['', '', '', ''],
    correctOptionIndex: null,
    type: 'MultipleChoice'
  });
  
  // File lesson states
  const [showFileLessonForm, setShowFileLessonForm] = useState(false);
  const [fileLessonTitle, setFileLessonTitle] = useState('');
  const [lessonFiles, setLessonFiles] = useState<LessonFile[]>([]);
  const [newFileName, setNewFileName] = useState('');
  const [newFileUrl, setNewFileUrl] = useState('');

  // Edit unit states
  const [isEditingUnit, setIsEditingUnit] = useState(false);
  const [editUnitName, setEditUnitName] = useState('');
  const [editUnitTitel, setEditUnitTitel] = useState('');
  const [editIsIndividual, setEditIsIndividual] = useState(true);
  const [editActive, setEditActive] = useState(true);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);

  // Read URL params to set initial tab
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab') as 'content' | 'details' | 'students' | null;
    if (tabParam && ['content', 'details', 'students'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    navigate(`?${params.toString()}`, { replace: true });
  }, [activeTab, location.search, navigate]);

  // Fetch unit details and lessons
  const fetchUnitDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [unitResponse, lessonsResponse] = await Promise.all([
        authFetch(apiUrl(`Unit/${unitId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }),
        authFetch(apiUrl(`unit/${unitId}/lessons/all`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        })
      ]);

      const unitData = await unitResponse.json();
      if (unitData.success && unitData.data) {
        setUnit(unitData.data);
      } else {
        setError(unitData.message || 'فشل في تحميل بيانات المحاضرة');
        return;
      }

      const lessonsData = await lessonsResponse.json();
      if (lessonsData.success) {
        const rawLessons = lessonsData.data?.lessons || lessonsData.data?.Lessons || [];
        const transformedLessons = rawLessons.map((lesson: any) => ({
          id: lesson.id,
          title: lesson.lessonName || lesson.titel || lesson.title,
          type: lesson.type,
          isVisible: lesson.active,
          order: lesson.order
        }));
        setLessons(transformedLessons);
      } else {
        // New units may have no lessons yet — treat as empty list
        setLessons([]);
      }
    } catch (err: any) {
      console.error('Error fetching unit details:', err);
      setError(err.message || 'حدث خطأ أثناء تحميل بيانات المحاضرة');
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  const startEditingUnit = () => {
    if (!unit) return;
    setEditUnitName(unit.unitName || unit.unitTitle || unit.titel || '');
    setEditUnitTitel(unit.titel || unit.unitTitle || '');
    setEditIsIndividual(unit.isIndividual ?? true);
    setEditActive(unit.active ?? true);
    setEditImageFile(null);
    if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    setEditImagePreview(null);
    setIsEditingUnit(true);
  };

  const cancelEditingUnit = () => {
    setIsEditingUnit(false);
    setEditImageFile(null);
    if (editImagePreview) URL.revokeObjectURL(editImagePreview);
    setEditImagePreview(null);
  };

  const handleUpdateUnit = async () => {
    if (!unit || !unitId) return;
    if (!editUnitName.trim()) {
      setSaveMessage({ type: 'error', text: 'يرجى إدخال اسم المحاضرة' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      setIsSaving(true);

      let imagePath = unit.imagePath || undefined;
      if (editImageFile) {
        const uploadResponse = await unitApi.uploadImage(editImageFile);
        if (!uploadResponse.success) {
          setSaveMessage({
            type: 'error',
            text: uploadResponse.message || 'فشل رفع صورة المحاضرة'
          });
          return;
        }
        imagePath = uploadResponse.data?.imagePath;
      }

      const response = await authFetch(apiUrl(`Unit/${unitId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          unitName: editUnitName.trim(),
          titel: editUnitTitel.trim() || editUnitName.trim(),
          courseId: unit.courseId || null,
          teacherId: unit.teacherId || null,
          gradeId: unit.gradeId || null,
          active: editActive,
          isIndividual: editIsIndividual,
          ...(imagePath ? { imagePath } : {})
        })
      });

      const data = await response.json();
      if (data.success) {
        setSaveMessage({ type: 'success', text: 'تم تحديث المحاضرة بنجاح' });
        setIsEditingUnit(false);
        setEditImageFile(null);
        if (editImagePreview) URL.revokeObjectURL(editImagePreview);
        setEditImagePreview(null);
        await fetchUnitDetails();
      } else {
        setSaveMessage({ type: 'error', text: data.message || 'فشل تحديث المحاضرة' });
      }
    } catch (err: any) {
      setSaveMessage({ type: 'error', text: err.message || 'حدث خطأ أثناء تحديث المحاضرة' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  useEffect(() => {
    if (unitId) {
      fetchUnitDetails();
    }
  }, [unitId, fetchUnitDetails]);

  // Fetch enrolled students in unit
  const fetchEnrolledStudents = useCallback(async (filterOverride?: { searchTerm?: string; name?: string }) => {
    if (!unitId) return;
    
    setLoadingStudents(true);
    setStudentsError(null);
    
    const filters = filterOverride ?? studentFilters;
    
    try {
      // Use the correct API endpoint: GET /api/unit/{unitId}/students
      let url = apiUrl(`Unit/${unitId}/students`);
      
      const params = [];
      params.push(`pageNumber=${studentCurrentPage}`);
      params.push(`pageSize=${studentPageSize}`);
      
      if (filters.searchTerm || filters.name) {
        if (filters.searchTerm) {
          params.push(`searchTerm=${encodeURIComponent(filters.searchTerm)}`);
        }
        if (filters.name) {
          params.push(`name=${encodeURIComponent(filters.name)}`);
        }
      }
      
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await authFetch(url, {
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch enrolled students');
      }
      
      const result = await response.json();
      
      if (result.success) {
        const paginatedData = result.data;
        const students = paginatedData.data || [];
        setEnrolledStudents(students);
        setStudentTotalCount(paginatedData.totalCount || 0);
        setStudentTotalPages(paginatedData.totalPages || 1);
        
        if (studentCurrentPage > (paginatedData.totalPages || 1)) {
          setStudentCurrentPage(1);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch enrolled students');
      }
    } catch (error: any) {
      console.error('Error fetching enrolled students:', error);
      setStudentsError(error.message || 'Failed to load enrolled students.');
      setEnrolledStudents([]);
      setStudentTotalCount(0);
      setStudentTotalPages(1);
    } finally {
      setLoadingStudents(false);
    }
  }, [unitId, studentFilters, studentCurrentPage, studentPageSize]);

  // Fetch students when students tab is active
  useEffect(() => {
    if (activeTab === 'students') {
      fetchEnrolledStudents();
    }
  }, [activeTab, unitId, studentCurrentPage, fetchEnrolledStudents]);

  // Student filter functions
  const handleStudentFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStudentFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyStudentFilters = () => {
    setStudentCurrentPage(1);
    fetchEnrolledStudents();
  };

  const resetStudentFilters = () => {
    setStudentFilters({
      searchTerm: '',
      name: ''
    });
    setStudentCurrentPage(1);
    fetchEnrolledStudents({ searchTerm: '', name: '' });
  };

  // Search for student by mobile number
  const handleSearchStudent = async () => {
    if (!enrollStudentData.trim()) {
      setEnrollStudentError('يرجى إدخال رقم الهاتف');
      return;
    }

    setIsSearchingStudent(true);
    setEnrollStudentError(null);
    setSearchedStudent(null);

    try {
      const response = await authFetch(
        apiUrl(`AdminStudent/Studentsearch?phonenumber=${encodeURIComponent(enrollStudentData)}`),
        {
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (result.success && result.data && result.data.length > 0) {
        setSearchedStudent(result.data[0]);
        setEnrollStudentError(null);
      } else {
        setEnrollStudentError('لم يتم العثور على طالب بهذا الرقم');
        setSearchedStudent(null);
      }
    } catch (error) {
      console.error('Error searching student:', error);
      setEnrollStudentError('فشل في البحث عن الطالب. يرجى المحاولة مرة أخرى.');
      setSearchedStudent(null);
    } finally {
      setIsSearchingStudent(false);
    }
  };

  // Enroll student in unit
  const handleEnrollStudent = async () => {
    if (!searchedStudent) {
      setEnrollStudentError('يرجى البحث عن طالب أولاً');
      return;
    }

    setIsSaving(true);
    setEnrollStudentError(null);

    try {
      // Use the correct API endpoint: POST /api/unit/{unitId}/enroll-student
      const response = await authFetch(
        apiUrl(`Unit/${unitId}/enroll-student`),
        {
          method: 'POST',
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({
            studentId: searchedStudent.id
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم تسجيل الطالب في المحاضرة بنجاح'
        });
        setShowEnrollModal(false);
        setEnrollStudentData('');
        setSearchedStudent(null);
        fetchEnrolledStudents();
      } else {
        setEnrollStudentError(result.message || 'فشل في تسجيل الطالب');
      }
    } catch (error) {
      console.error('Error enrolling student:', error);
      setEnrollStudentError('فشل في تسجيل الطالب. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Remove student from unit
  // Note: This API endpoint is not documented in UNITS_LESSONS_API_DOCUMENTATION.md
  // Expected endpoint: DELETE /api/unit/{unitId}/unenroll-student/{studentId} or similar
  // You may need to add this endpoint to the backend if it doesn't exist
  const handleRemoveStudent = async (studentId: string) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في إزالة هذا الطالب من المحاضرة؟')) {
      return;
    }

    setIsSaving(true);

    try {
      // Attempt to unenroll student from unit
      // Note: This endpoint may not exist yet in the API
      const response = await authFetch(
        apiUrl(`Unit/${unitId}/unenroll-student/${studentId}`),
        {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('API endpoint for unenrolling from unit not found. This feature may need to be added to the backend.');
        }
        throw new Error('Failed to remove student');
      }

      const result = await response.json();

      if (result.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم إزالة الطالب من المحاضرة بنجاح'
        });
        fetchEnrolledStudents();
      } else {
        setStudentsError('Failed to remove student from unit: ' + (result.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error removing student:', error);
      setStudentsError(error.message || 'فشل في إزالة الطالب. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Handle add lesson click
  const handleAddLessonClick = () => {
    setShowLessonTypeModal(true);
  };

  // Handle lesson click (view lesson details)
  const handleLessonClick = (e: any, lesson: Lesson) => {
    e.stopPropagation();
    
    if (lesson.type.toLowerCase() === 'assignment') {
      navigate(`/assignment/without-course/${unitId}?lessonId=${lesson.id}`);
    } else {
      // For other lesson types, navigate to lesson view (need to implement or handle accordingly)
      // For now, just log it
      console.log('View lesson:', lesson);
    }
  };

  // Handle delete lesson
  const handleDeleteLessonConfirm = (lessonId: number) => {
    setLessonToAction({ lessonId });
    setShowDeleteLessonModal(true);
  };

  const confirmDeleteLesson = async () => {
    if (lessonToAction) {
      setShowDeleteLessonModal(false);
      setLessonToAction(null);
      
      try {
        setIsSaving(true);
        
        const response = await authFetch(apiUrl(`Lesson/${lessonToAction.lessonId}`), {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Update local state
          setLessons(lessons.filter(lesson => lesson.id !== lessonToAction.lessonId));
          setSaveMessage({
            type: 'success',
            text: 'تم حذف الدرس بنجاح'
          });
        } else {
          setSaveMessage({
            type: 'error',
            text: data.message || 'فشل في حذف الدرس'
          });
        }
      } catch (error) {
        console.error('Error deleting lesson:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في حذف الدرس. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        setTimeout(() => setSaveMessage(null), 3000);
      }
    }
  };

  // Handle hide/show lesson
  const handleHideLessonConfirm = (lessonId: number) => {
    setLessonToAction({ lessonId });
    setShowHideLessonModal(true);
  };

  const confirmHideLesson = async () => {
    if (lessonToAction) {
      setShowHideLessonModal(false);
      setLessonToAction(null);
      
      try {
        setIsSaving(true);
        const lessonIndex = lessons.findIndex(lesson => lesson.id === lessonToAction.lessonId);
        
        if (lessonIndex !== -1) {
          const newVisibility = !lessons[lessonIndex].isVisible;
          const lesson = lessons[lessonIndex];
          
          // Fetch current lesson details
          const lessonDetailsResponse = await authFetch(apiUrl(`Lesson/${lessonToAction.lessonId}`), {
            method: 'GET',
            headers: {
              'accept': '*/*',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          });
          
          const lessonDetails = await lessonDetailsResponse.json();
          
          if (!lessonDetails.success) {
            throw new Error('Failed to fetch lesson details');
          }
          
          // Prepare payload
          const lessonPayload: any = {
            lessonName: lesson.title,
            titel: lesson.title,
            unitId: parseInt(unitId!),
            active: newVisibility,
            order: lesson.order,
            type: lesson.type === 'video' ? 'Video' 
              : lesson.type === 'file' ? 'File' 
              : lesson.type === 'assignment' ? 'Assignment'
              : 'Quiz',
            videoUrl: lessonDetails.data?.videoUrl || null,
            attachmentTitel: lessonDetails.data?.attachmentTitel || null,
            attachmentUrl: lessonDetails.data?.attachmentUrl || null,
            questions: lessonDetails.data?.questions || []
          };

          if (lesson.type === 'file') {
            lessonPayload.files = lessonDetails.data?.files || [];
          }
          
          // Update lesson
          const response = await authFetch(apiUrl(`Lesson/${lessonToAction.lessonId}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'accept': '*/*',
              'Authorization': `Bearer ${authService.getToken()}`
            },
            body: JSON.stringify(lessonPayload)
          });
          
          const data = await response.json();
          
          if (data.success) {
            const updatedLessons = [...lessons];
            updatedLessons[lessonIndex].isVisible = newVisibility;
            setLessons(updatedLessons);
            setSaveMessage({
              type: 'success',
              text: `تم ${newVisibility ? 'إظهار' : 'إخفاء'} الدرس بنجاح`
            });
          } else {
            setSaveMessage({
              type: 'error',
              text: data.message || 'فشل في تحديث رؤية الدرس'
            });
          }
        }
      } catch (error) {
        console.error('Error updating lesson visibility:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في تحديث رؤية الدرس. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        setTimeout(() => setSaveMessage(null), 3000);
      }
    }
  };

  // Handle view quiz results
  const handleViewQuizResults = (lessonId: number, e: any) => {
    e.stopPropagation();
    // Navigate to quiz results page or show modal
    console.log('View quiz results for lesson:', lessonId);
  };

  // Handle view student completion
  const handleViewStudentCompletion = (lessonId: number, e: any) => {
    e.stopPropagation();
    // Navigate to student completion page or show modal
    console.log('View student completion for lesson:', lessonId);
  };

  // Handle add video lesson
  const handleAddVideoLesson = () => {
    setShowLessonTypeModal(false);
    setShowVideoLessonForm(true);
  };

  // Handle add quiz lesson
  const handleAddQuizLesson = () => {
    setShowLessonTypeModal(false);
    setShowQuizForm(true);
    setQuizTitle('');
    setQuestions([]);
    setCurrentQuestion({
      id: 1,
      text: '',
      options: ['', '', '', ''],
      correctOptionIndex: null,
      type: 'MultipleChoice'
    });
  };

  // Handle add file lesson
  const handleAddFileLesson = () => {
    setShowLessonTypeModal(false);
    setShowFileLessonForm(true);
    setFileLessonTitle('');
    setLessonFiles([]);
    setNewFileName('');
    setNewFileUrl('');
  };

  // Handle create video lesson
  const handleCreateVideoLesson = async () => {
    if (!videoLessonTitle || !videoUrl) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى ملء جميع الحقول المطلوبة'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      const lastOrder = lessons.length > 0 
        ? Math.max(...lessons.map(l => l.order || 0)) 
        : 0;

      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: videoLessonTitle,
          titel: videoLessonTitle,
          unitId: parseInt(unitId!),
          active: true,
          order: lastOrder + 1,
          type: 'Video',
          videoUrl: videoUrl,
        })
      });

      const data = await response.json();

      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة الدرس بنجاح'
        });
        setShowVideoLessonForm(false);
        setVideoLessonTitle('');
        setVideoUrl('');
        fetchUnitDetails(); // Refresh the lessons
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة الدرس'
        });
      }
    } catch (err: any) {
      setSaveMessage({
        type: 'error',
        text: err.message || 'حدث خطأ أثناء إضافة الدرس'
      });
    } finally {
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Handle create quiz lesson
  const handleCreateQuizLesson = async () => {
    if (!quizTitle || questions.length === 0) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال عنوان الاختبار وإضافة أسئلة'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      const lastOrder = lessons.length > 0 
        ? Math.max(...lessons.map(l => l.order || 0)) 
        : 0;

      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: quizTitle,
          titel: quizTitle,
          unitId: parseInt(unitId!),
          active: true,
          order: lastOrder + 1,
          type: 'Quiz',
          questions: questions.map(q => ({
            text: q.text,
            options: q.options,
            correctOptionIndex: q.correctOptionIndex,
            type: q.type
          }))
        })
      });

      const data = await response.json();

      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة الاختبار بنجاح'
        });
        setShowQuizForm(false);
        setQuizTitle('');
        setQuestions([]);
        fetchUnitDetails(); // Refresh the lessons
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة الاختبار'
        });
      }
    } catch (err: any) {
      setSaveMessage({
        type: 'error',
        text: err.message || 'حدث خطأ أثناء إضافة الاختبار'
      });
    } finally {
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Handle create file lesson
  const handleCreateFileLesson = async () => {
    if (!fileLessonTitle || lessonFiles.length === 0) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال عنوان الدرس وإضافة ملفات'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      const lastOrder = lessons.length > 0 
        ? Math.max(...lessons.map(l => l.order || 0)) 
        : 0;

      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: fileLessonTitle,
          titel: fileLessonTitle,
          unitId: parseInt(unitId!),
          active: true,
          order: lastOrder + 1,
          type: 'File',
          files: lessonFiles.map((file, index) => ({
            fileName: file.fileName,
            fileUrl: file.fileUrl,
            order: index + 1
          }))
        })
      });

      const data = await response.json();

      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة الدرس بنجاح'
        });
        setShowFileLessonForm(false);
        setFileLessonTitle('');
        setLessonFiles([]);
        fetchUnitDetails(); // Refresh the lessons
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة الدرس'
        });
      }
    } catch (err: any) {
      setSaveMessage({
        type: 'error',
        text: err.message || 'حدث خطأ أثناء إضافة الدرس'
      });
    } finally {
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Quiz question management functions
  const handleAddQuestion = () => {
    if (!currentQuestion.text || currentQuestion.correctOptionIndex === null) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى ملء جميع حقول السؤال'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setQuestions([...questions, currentQuestion]);
    setCurrentQuestion({
      id: questions.length + 2,
      text: '',
      options: ['', '', '', ''],
      correctOptionIndex: null,
      type: 'MultipleChoice'
    });
  };

  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  // File management functions
  const handleAddFile = () => {
    if (!newFileName || !newFileUrl) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال اسم الملف والرابط'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setLessonFiles([...lessonFiles, {
      fileName: newFileName,
      fileUrl: newFileUrl,
      order: lessonFiles.length + 1
    }]);
    setNewFileName('');
    setNewFileUrl('');
  };

  const handleRemoveFile = (index: number) => {
    setLessonFiles(lessonFiles.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-600 text-center">
          <p className="text-xl font-bold mb-2">خطأ</p>
          <p>{error}</p>
          <button
            onClick={() => navigate('/lectures')}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            العودة إلى المحاضرات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/lectures')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5 ml-2" />
          العودة إلى المحاضرات
        </button>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {unit?.unitName || unit?.unitTitle || unit?.titel || 'تفاصيل المحاضرة'}
        </h1>
        <p className="text-gray-600">إدارة المحاضرة</p>
      </div>

      {/* Success/Error Message */}
      {saveMessage && (
        <div className={`mb-4 p-4 rounded-lg ${
          saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {saveMessage.text}
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'content'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              المحتوى
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              تفاصيل المحاضرة
            </button>
            <button
              onClick={() => setActiveTab('students')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'students'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              الطلاب المسجلون
            </button>
          </nav>
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="p-6">
            {/* Add Lesson Button */}
            <div className="mb-6">
              <button
                onClick={handleAddLessonClick}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                إضافة درس
              </button>
            </div>

            {/* Lessons List */}
            <div>
              <h2 className="text-xl font-bold mb-4">الدروس ({lessons.length})</h2>
              {lessons.length === 0 ? (
                <p className="text-gray-500 text-center py-8">لا توجد دروس في هذه المحاضرة</p>
              ) : (
                <ul className="space-y-2">
                  {lessons.map((lesson) => (
                    <li key={lesson.id} className="p-2 border-b">
                      <div className="flex justify-between items-center">
                        <div 
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={(e: any) => handleLessonClick(e, lesson)}
                        >
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            lesson.type.toLowerCase() === 'video' ? 'bg-blue-100 text-blue-800' 
                              : lesson.type.toLowerCase() === 'file' ? 'bg-red-100 text-red-800'
                              : lesson.type.toLowerCase() === 'assignment' ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {lesson.type.toLowerCase() === 'video' ? 'فيديو' 
                              : lesson.type.toLowerCase() === 'file' ? 'PDF'
                              : lesson.type.toLowerCase() === 'assignment' ? 'واجب'
                              : 'اختبار'}
                          </span>
                          <span>{lesson.title}</span>
                          {!lesson.isVisible && (
                            <span className="text-xs text-red-500 mr-2">(مخفي)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {lesson.type.toLowerCase() === 'quiz' && (
                            <button
                              onClick={(e: any) => handleViewQuizResults(lesson.id, e)}
                              className="text-indigo-600 hover:text-indigo-800"
                              title="عرض نتائج الاختبار"
                            >
                              <DocumentTextIcon className="w-5 h-5" />
                            </button>
                          )}
                          {lesson.type.toLowerCase() === 'video' && (
                            <button
                              onClick={(e: any) => handleViewStudentCompletion(lesson.id, e)}
                              className="text-blue-600 hover:text-blue-800"
                              title="عرض حالة مشاهدة الطلاب"
                            >
                              <UserIcon className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleHideLessonConfirm(lesson.id)}
                            className={`${
                              lesson.isVisible ? 'text-gray-600' : 'text-red-600'
                            } hover:text-gray-900`}
                            title={lesson.isVisible ? 'إخفاء الدرس' : 'إظهار الدرس'}
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteLessonConfirm(lesson.id)}
                            className="text-red-600 hover:text-red-700"
                            title="حذف الدرس"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">تفاصيل المحاضرة</h2>
              {unit && !isEditingUnit && (
                <button
                  type="button"
                  onClick={startEditingUnit}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  تعديل
                </button>
              )}
            </div>
            {unit && !isEditingUnit && (
              <div className="space-y-4">
                {unit.imagePath && getImageUrl(unit.imagePath) && (
                  <img
                    src={getImageUrl(unit.imagePath)!}
                    alt={unit.unitName}
                    className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                  />
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">اسم المحاضرة</label>
                    <p className="text-gray-900">{unit.unitName || unit.unitTitle || unit.titel}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">العنوان الفرعي</label>
                    <p className="text-gray-900">{unit.titel || unit.unitTitle || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">السعر</label>
                    <p className="text-gray-900">{unit.price} جنيه</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
                    <p className={`text-sm ${unit.active ? 'text-green-600' : 'text-red-600'}`}>
                      {unit.active ? 'نشط' : 'غير نشط'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الإنشاء</label>
                    <p className="text-gray-900">{new Date(unit.creationDate).toLocaleDateString('ar-EG')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الترتيب</label>
                    <p className="text-gray-900">{unit.order}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">محاضرة فردية</label>
                    <p className="text-gray-900">{unit.isIndividual ? 'نعم' : 'لا'}</p>
                  </div>
                </div>
              </div>
            )}
            {unit && isEditingUnit && (
              <div className="space-y-4 max-w-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">اسم المحاضرة *</label>
                  <input
                    type="text"
                    value={editUnitName}
                    onChange={(e) => setEditUnitName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">العنوان الفرعي</label>
                  <input
                    type="text"
                    value={editUnitTitel}
                    onChange={(e) => setEditUnitTitel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="editActive"
                    type="checkbox"
                    checked={editActive}
                    onChange={(e) => setEditActive(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="editActive" className="text-sm font-medium text-gray-700">نشط</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="editIsIndividual"
                    type="checkbox"
                    checked={editIsIndividual}
                    onChange={(e) => setEditIsIndividual(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="editIsIndividual" className="text-sm font-medium text-gray-700">
                    محاضرة فردية (بدون باقة)
                  </label>
                </div>
                {(unit.imagePath || editImagePreview) && (
                  <img
                    src={editImagePreview || getImageUrl(unit.imagePath)!}
                    alt="معاينة"
                    className="h-24 w-24 object-cover rounded-lg border border-gray-200"
                  />
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تغيير الصورة</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (editImagePreview) URL.revokeObjectURL(editImagePreview);
                      setEditImageFile(file);
                      setEditImagePreview(file ? URL.createObjectURL(file) : null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleUpdateUnit}
                    disabled={isSaving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditingUnit}
                    disabled={isSaving}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {canViewStudentCounts
                  ? `الطلاب المسجلون (${studentTotalCount})`
                  : 'الطلاب المسجلون'}
              </h2>
              <button
                onClick={() => setShowEnrollModal(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                تسجيل طالب
              </button>
            </div>

            {/* Search Filters */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    البحث (رقم الهاتف)
                  </label>
                  <input
                    type="text"
                    name="searchTerm"
                    value={studentFilters.searchTerm}
                    onChange={handleStudentFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ابحث برقم الهاتف"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الاسم
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={studentFilters.name}
                    onChange={handleStudentFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ابحث بالاسم"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={applyStudentFilters}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    بحث
                  </button>
                  <button
                    onClick={resetStudentFilters}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    إعادة تعيين
                  </button>
                </div>
              </div>
            </div>

            {/* Students List */}
            {loadingStudents ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : studentsError ? (
              <div className="text-red-600 text-center py-8">{studentsError}</div>
            ) : enrolledStudents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الاسم</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">البريد الإلكتروني</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">رقم الهاتف</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاريخ التسجيل</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {enrolledStudents.map((student) => (
                      <tr key={student.studentId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.studentName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.phoneNumber || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.enrollmentDate ? new Date(student.enrollmentDate).toLocaleDateString('ar-EG') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleRemoveStudent(student.studentId)}
                            className="text-red-600 hover:text-red-900"
                            disabled={isSaving}
                          >
                            إزالة
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {studentTotalPages > 1 && (
                  <div className="mt-4 flex justify-center items-center gap-2">
                    <button
                      onClick={() => setStudentCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={studentCurrentPage === 1}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50"
                    >
                      السابق
                    </button>
                    <span className="text-gray-600">
                      صفحة {studentCurrentPage} من {studentTotalPages}
                    </span>
                    <button
                      onClick={() => setStudentCurrentPage(prev => Math.min(studentTotalPages, prev + 1))}
                      disabled={studentCurrentPage === studentTotalPages}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50"
                    >
                      التالي
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">لا يوجد طلاب مسجلون في هذه المحاضرة</p>
            )}
          </div>
        )}
      </div>

      {/* Enroll Student Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">تسجيل طالب جديد</h2>
              <button
                onClick={() => {
                  setShowEnrollModal(false);
                  setEnrollStudentData('');
                  setSearchedStudent(null);
                  setEnrollStudentError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رقم هاتف الطالب
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={enrollStudentData}
                    onChange={(e) => setEnrollStudentData(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أدخل رقم الهاتف"
                  />
                  <button
                    onClick={handleSearchStudent}
                    disabled={isSearchingStudent}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSearchingStudent ? 'جاري البحث...' : 'بحث'}
                  </button>
                </div>
              </div>

              {enrollStudentError && (
                <div className="text-red-600 text-sm">{enrollStudentError}</div>
              )}

              {searchedStudent && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">معلومات الطالب</h3>
                  <p className="text-sm text-gray-600">الاسم: {searchedStudent.fullName}</p>
                  <p className="text-sm text-gray-600">البريد الإلكتروني: {searchedStudent.email}</p>
                  <p className="text-sm text-gray-600">رقم الهاتف: {searchedStudent.phoneNumber}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEnrollModal(false);
                  setEnrollStudentData('');
                  setSearchedStudent(null);
                  setEnrollStudentError(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleEnrollStudent}
                disabled={!searchedStudent || isSaving}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'جاري التسجيل...' : 'تسجيل'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Type Modal */}
      {showLessonTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6 text-center">اختر نوع الدرس</h2>
            <div className="grid grid-cols-3 gap-6">
              <div 
                onClick={handleAddVideoLesson}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-blue-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium">درس فيديو</span>
              </div>
              <div 
                onClick={handleAddQuizLesson}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-green-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium">اختبار</span>
              </div>
              <div 
                onClick={handleAddFileLesson}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-red-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </div>
                <span className="font-medium">PDF</span>
              </div>
              <div 
                onClick={() => {
                  setShowLessonTypeModal(false);
                  navigate(`/assignment/without-course/${unitId}`);
                }}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-yellow-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-medium">واجب</span>
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setShowLessonTypeModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Lesson Form */}
      {showVideoLessonForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-2xl font-bold mb-6">إضافة درس فيديو</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان الدرس *
                </label>
                <input
                  type="text"
                  value={videoLessonTitle}
                  onChange={(e) => setVideoLessonTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="أدخل عنوان الدرس"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط الفيديو *
                </label>
                <input
                  type="text"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="أدخل رابط الفيديو"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowVideoLessonForm(false);
                  setVideoLessonTitle('');
                  setVideoUrl('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateVideoLesson}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                إضافة الدرس
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Form */}
      {showQuizForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8">
            <h2 className="text-2xl font-bold mb-6">إضافة اختبار</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان الاختبار *
                </label>
                <input
                  type="text"
                  value={quizTitle}
                  onChange={(e) => setQuizTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="أدخل عنوان الاختبار"
                />
              </div>

              {/* Display added questions */}
              {questions.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">الأسئلة المضافة ({questions.length})</h3>
                  <div className="space-y-2">
                    {questions.map((q, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                        <span className="text-sm">{index + 1}. {q.text}</span>
                        <button
                          onClick={() => handleRemoveQuestion(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current question form */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">إضافة سؤال جديد</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      نص السؤال *
                    </label>
                    <input
                      type="text"
                      value={currentQuestion.text}
                      onChange={(e) => setCurrentQuestion({...currentQuestion, text: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="أدخل نص السؤال"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      نوع السؤال
                    </label>
                    <select
                      value={currentQuestion.type}
                      onChange={(e) => setCurrentQuestion({
                        ...currentQuestion, 
                        type: e.target.value as 'MultipleChoice' | 'TrueFalse',
                        options: e.target.value === 'TrueFalse' ? ['صح', 'خطأ'] : ['', '', '', '']
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="MultipleChoice">اختيار من متعدد</option>
                      <option value="TrueFalse">صح/خطأ</option>
                    </select>
                  </div>
                  {currentQuestion.type === 'MultipleChoice' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الخيارات *
                      </label>
                      {currentQuestion.options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                          <input
                            type="radio"
                            name="correctOption"
                            checked={currentQuestion.correctOptionIndex === index}
                            onChange={() => setCurrentQuestion({...currentQuestion, correctOptionIndex: index})}
                            className="w-4 h-4"
                          />
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...currentQuestion.options];
                              newOptions[index] = e.target.value;
                              setCurrentQuestion({...currentQuestion, options: newOptions});
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={`الخيار ${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {currentQuestion.type === 'TrueFalse' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الإجابة الصحيحة *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="trueFalse"
                            checked={currentQuestion.correctOptionIndex === 0}
                            onChange={() => setCurrentQuestion({...currentQuestion, correctOptionIndex: 0})}
                            className="w-4 h-4"
                          />
                          <span>صح</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="trueFalse"
                            checked={currentQuestion.correctOptionIndex === 1}
                            onChange={() => setCurrentQuestion({...currentQuestion, correctOptionIndex: 1})}
                            className="w-4 h-4"
                          />
                          <span>خطأ</span>
                        </label>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={handleAddQuestion}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    إضافة السؤال
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowQuizForm(false);
                  setQuizTitle('');
                  setQuestions([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateQuizLesson}
                disabled={questions.length === 0}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                إضافة الاختبار
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Lesson Form */}
      {showFileLessonForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full my-8">
            <h2 className="text-2xl font-bold mb-6">إضافة درس PDF</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان الدرس *
                </label>
                <input
                  type="text"
                  value={fileLessonTitle}
                  onChange={(e) => setFileLessonTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="أدخل عنوان الدرس"
                />
              </div>

              {/* Display added files */}
              {lessonFiles.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">الملفات المضافة ({lessonFiles.length})</h3>
                  <div className="space-y-2">
                    {lessonFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                        <span className="text-sm">{file.fileName}</span>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add file form */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">إضافة ملف جديد</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      اسم الملف *
                    </label>
                    <input
                      type="text"
                      value={newFileName}
                      onChange={(e) => setNewFileName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="أدخل اسم الملف"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      رابط الملف *
                    </label>
                    <input
                      type="text"
                      value={newFileUrl}
                      onChange={(e) => setNewFileUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="أدخل رابط الملف"
                    />
                  </div>
                  <button
                    onClick={handleAddFile}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    إضافة الملف
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowFileLessonForm(false);
                  setFileLessonTitle('');
                  setLessonFiles([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateFileLesson}
                disabled={lessonFiles.length === 0}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                إضافة الدرس
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Lesson Confirmation Modal */}
      {showDeleteLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">تأكيد حذف الدرس</h2>
            <p className="text-gray-600 mb-6">هل أنت متأكد من رغبتك في حذف هذا الدرس؟ لا يمكن التراجع عن هذا الإجراء.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteLessonModal(false);
                  setLessonToAction(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isSaving}
              >
                إلغاء
              </button>
              <button
                onClick={confirmDeleteLesson}
                disabled={isSaving}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'جاري الحذف...' : 'حذف'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide/Show Lesson Confirmation Modal */}
      {showHideLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">
              {lessonToAction && lessons.find(l => l.id === lessonToAction.lessonId)?.isVisible 
                ? 'تأكيد إخفاء الدرس' 
                : 'تأكيد إظهار الدرس'}
            </h2>
            <p className="text-gray-600 mb-6">
              {lessonToAction && lessons.find(l => l.id === lessonToAction.lessonId)?.isVisible
                ? 'هل أنت متأكد من رغبتك في إخفاء هذا الدرس؟ لن يتمكن الطلاب من رؤيته.'
                : 'هل أنت متأكد من رغبتك في إظهار هذا الدرس؟ سيتمكن الطلاب من رؤيته.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowHideLessonModal(false);
                  setLessonToAction(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isSaving}
              >
                إلغاء
              </button>
              <button
                onClick={confirmHideLesson}
                disabled={isSaving}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'جاري التحديث...' : 'تأكيد'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LectureDetail;
